import { refreshTokenFunc } from './auth';
import secureStorage from '../utils/secureStorage';

const TOKEN_KEYS = ['authToken', 'idToken', 'refreshToken', 'tokenExpiry'];

class TokenManager {
  constructor() {
    this.isRefreshing = false;
    this.refreshPromise = null;
    this.subscribers = [];
    this.checkInterval = null;
    this.useSecureStorage = true; // Enable secure storage by default

    // In-memory cache of decrypted tokens. This is what the synchronous
    // accessors read, so plaintext tokens never need to live in localStorage.
    // Hydrated by getTokenInfo() during auth initialization.
    this.memoryCache = new Map();
  }

  // Subscribe to token refresh events
  subscribe(callback) {
    this.subscribers.push(callback);
    return () => {
      this.subscribers = this.subscribers.filter(cb => cb !== callback);
    };
  }

  // Notify all subscribers of token events
  notify(event, data) {
    this.subscribers.forEach(callback => {
      try {
        callback(event, data);
      } catch (error) {
        console.error('Token event callback error:', error);
      }
    });
  }

  _buildTokenInfo(authToken, idToken, refreshToken, tokenExpiry) {
    const expiryTime = tokenExpiry ? parseInt(tokenExpiry, 10) : null;
    const isValidExpiry = expiryTime && !isNaN(expiryTime);
    const isExpired = isValidExpiry ? Date.now() > expiryTime : true;
    const timeUntilExpiry = isValidExpiry ? expiryTime - Date.now() : 0;

    return {
      authToken,
      idToken,
      refreshToken,
      tokenExpiry: expiryTime,
      isValidExpiry,
      isExpired,
      timeUntilExpiry,
      hasValidTokens: !!(authToken && idToken && refreshToken && isValidExpiry && !isExpired)
    };
  }

  // Get current token info (async; reads secure storage and hydrates the memory cache)
  async getTokenInfo() {
    const authToken = await this.getTokenSecure('authToken');
    const idToken = await this.getTokenSecure('idToken');
    const refreshToken = await this.getTokenSecure('refreshToken');
    const tokenExpiry = await this.getTokenSecure('tokenExpiry');

    return this._buildTokenInfo(authToken, idToken, refreshToken, tokenExpiry);
  }

  // Synchronous token info from the in-memory cache. Only valid after
  // getTokenInfo() (or a sign-in) has hydrated the cache.
  getTokenInfoSync() {
    return this._buildTokenInfo(
      this.memoryCache.get('authToken') || null,
      this.memoryCache.get('idToken') || null,
      this.memoryCache.get('refreshToken') || null,
      this.memoryCache.get('tokenExpiry') || null
    );
  }

  // Check if tokens need refresh (5 minutes before expiry) - uses sync version for immediate checks
  needsRefresh() {
    const { isExpired, timeUntilExpiry, hasValidTokens } = this.getTokenInfoSync();
    const fiveMinutes = 5 * 60 * 1000;

    if (!hasValidTokens || isExpired) {
      return { needed: false, reason: 'expired_or_invalid' };
    }

    if (timeUntilExpiry <= fiveMinutes) {
      return { needed: true, reason: 'expiring_soon', timeLeft: timeUntilExpiry };
    }

    return { needed: false, reason: 'still_valid', timeLeft: timeUntilExpiry };
  }

  // Refresh tokens (with deduplication)
  async refreshTokens() {
    const tokenInfo = this.getTokenInfoSync();

    if (!tokenInfo.refreshToken) {
      throw new Error('No refresh token available');
    }

    // If already refreshing, return the existing promise
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = this._performRefresh(tokenInfo.refreshToken);

    try {
      const result = await this.refreshPromise;
      this.notify('refresh_success', result);
      return result;
    } catch (error) {
      this.notify('refresh_failed', error);
      throw error;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  // Internal refresh logic
  async _performRefresh(refreshTokenValue) {
    try {
      const response = await refreshTokenFunc(refreshTokenValue);
      const { AccessToken, IdToken, RefreshToken, tokenExpiry } = response;

      await this.setTokenSecure('authToken', AccessToken);
      await this.setTokenSecure('idToken', IdToken);
      await this.setTokenSecure('refreshToken', RefreshToken);
      await this.setTokenSecure('tokenExpiry', tokenExpiry.toString());

      return {
        authToken: AccessToken,
        idToken: IdToken,
        refreshToken: RefreshToken,
        expiresAt: tokenExpiry
      };
    } catch (error) {
      console.error('Token refresh failed:', error);

      const refreshError = new Error(`Token refresh failed: ${error.message}`);

      // Only Cognito rejecting the refresh token means the tokens are dead.
      // Anything else (no network yet after waking from sleep, a timeout, an
      // SDK transport error) says nothing about the tokens, so keep them and
      // tag the error so callers can retry instead of signing the user out.
      if (error.name === 'NotAuthorizedException') {
        await this.clearTokens();
      } else {
        refreshError.transient = true;
      }
      throw refreshError;
    }
  }

  // Clear all tokens from memory, secure storage, and any legacy plaintext copies
  async clearTokens() {
    for (const key of [...TOKEN_KEYS, 'userEmail']) {
      await this.removeTokenSecure(key);
    }

    this.notify('tokens_cleared');
  }

  // Force logout (enhanced version)
  async forceLogout() {
    await this.clearTokens();
    this.stopMonitoring();
    this.notify('force_logout');

    // Redirect after a short delay
    setTimeout(() => {
      window.location.href = '/login';
    }, 100);
  }

  // Start monitoring token expiry
  startMonitoring(intervalMs = 5 * 60 * 1000) { // Default: check every 5 minutes
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }

    this.checkInterval = setInterval(() => {
      this._checkAndRefresh();
    }, intervalMs);

    // Check immediately
    this._checkAndRefresh();
  }

  // Stop monitoring
  stopMonitoring() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  // Internal monitoring check
  async _checkAndRefresh() {
    const tokenInfo = this.getTokenInfoSync();

    if (!tokenInfo.hasValidTokens) {
      this.forceLogout();
      return;
    }

    const refreshCheck = this.needsRefresh();

    if (refreshCheck.needed) {
      try {
        await this.refreshTokens();
      } catch (error) {
        console.error('Automatic token refresh failed:', error);
        this.forceLogout();
      }
    }
  }

  // Get current ID token for API calls - uses sync version for immediate access
  getIdToken() {
    const { idToken, hasValidTokens } = this.getTokenInfoSync();
    return hasValidTokens ? idToken : null;
  }

  // Get current access token - uses sync version for immediate access
  getAccessToken() {
    const { authToken, hasValidTokens } = this.getTokenInfoSync();
    return hasValidTokens ? authToken : null;
  }

  // Get user session identifier for secure storage key derivation.
  // Must be set (via setUserSession) BEFORE tokens are stored.
  getUserSession() {
    return localStorage.getItem('userEmail') || 'anonymous';
  }

  // Persist the session identifier used to derive the encryption key.
  setUserSession(email) {
    if (email) {
      localStorage.setItem('userEmail', email);
    }
  }

  // Secure token storage methods
  async setTokenSecure(key, value) {
    this.memoryCache.set(key, value);

    if (this.useSecureStorage) {
      try {
        await secureStorage.setItem(key, value, this.getUserSession());
        // Remove any legacy plaintext copy so it can't linger.
        localStorage.removeItem(key);
        return;
      } catch (error) {
        console.warn(`Secure storage for ${key} failed, falling back to localStorage:`, error);
      }
    }
    // Degraded mode only (e.g. no WebCrypto): plain localStorage.
    localStorage.setItem(key, value);
  }

  async getTokenSecure(key) {
    // Fast path: already in memory.
    if (this.memoryCache.has(key)) {
      return this.memoryCache.get(key);
    }

    if (this.useSecureStorage) {
      try {
        const value = await secureStorage.getItem(key, this.getUserSession());
        if (value !== null) {
          this.memoryCache.set(key, value);
          return value;
        }
      } catch (error) {
        console.warn(`Secure retrieval for ${key} failed:`, error);

        // If it's a decryption error, try to clear corrupted data
        if (error.message && error.message.includes('decrypt')) {
          try {
            secureStorage.clearCorruptedData();
          } catch (clearError) {
            console.error('Failed to clear corrupted data:', clearError);
          }
        }
      }
    }

    // Legacy fallback: migrate any old plaintext token into secure storage.
    const legacyValue = localStorage.getItem(key);
    if (legacyValue !== null) {
      await this.setTokenSecure(key, legacyValue);
    }
    return legacyValue;
  }

  async removeTokenSecure(key) {
    this.memoryCache.delete(key);
    if (this.useSecureStorage) {
      secureStorage.removeItem(key);
    }
    localStorage.removeItem(key);
  }
}

// Export singleton instance
export const tokenManager = new TokenManager();
export default tokenManager;
